import * as React from 'react';
import { useEffect, useState } from 'react';
import { Facility, FacilityType, Person, fieldValidation } from '../../../amorg-common';
import { deleteFacility, updateFacility } from '../../../api/locationClientApi';
import { getPersons } from '../../../api/personClientApi';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../../designSystem/Message';
import { Button } from '../../../designSystem/button/Button';
import { ButtonRow } from '../../../designSystem/button/ButtonRow';
import { Flex, FlexItem } from '../../../designSystem/compositeComponents/FlexHelpers';
import { SelectField } from '../../../designSystem/formField/SelectField';
import { TextSaveField } from '../../../designSystem/formField/text/TextSaveField';


type FacilityEditorProps = {
    facility: Facility,
    allFacilityTypes:FacilityType[],
    onFacilityUpdated: () => void,
    onFacilityDeleted: () => void,
    onFacilityClose: () => void,
}
export function FacilityEditor(props:FacilityEditorProps):JSX.Element
{
    const [loading, setLoading] = useState(false);
    const [persons, setPersons] = useState<Person[]>([]);
    
    const [errorMessage, setErrorMessage] = useState('');
    
    
    useEffect(() => {
        getPersonsForFacility();
    }, []);

    async function getPersonsForFacility(){
        const persons = await getPersons();
        setPersons(persons)
    }
    
    async function handleUpdateFacility(facility:Facility)
     {
        setLoading(true);
        await updateFacility(facility.id, facility.name, facility.building, facility.floor, facility.facilityType.id, facility.owner?.id || '')
        props.onFacilityUpdated();
        setLoading(false);
     }
    

    async function handleDeleteClick(){
        setErrorMessage('');
        const result = await deleteFacility(props.facility.id);
        
        if( result.isSuccess){
            props.onFacilityDeleted()
        }
        else if(result.errorMessage === 'deleteFacility_has_inspections'){
            setErrorMessage('Kan ikke slette faciliteten, da den er tilknyttet inspektioner for en sikkerhedsrundering')
        }
    }

    return (
        <>
            <Flex gap={24} flexDirection="column">
                <LoadingPageOverlay loading={loading} />

                {errorMessage && 
                    <MessageCard variant='error'>
                        {errorMessage}
                    </MessageCard>
                }

                <SelectField
                    label="Vælg en type for faciliteten" 
                    options={props.allFacilityTypes.map(c => { return {text:c.name, value:c.id}}).sort((a, b) => a.text.localeCompare(b.text))}
                    selectedValue={props.facility.facilityType.id}
                    onChange={ async (value)  => {
                        if(value !== props.facility.facilityType.id){
                            handleUpdateFacility({...props.facility, facilityType:{...props.facility.facilityType, id:value}});
                        }
                    }} />


                <TextSaveField
                    label={"Navn *"}
                    value={props.facility.name}
                    validator={(value)=> fieldValidation.facility.name(value) ? {valid:true} : {valid:false, errorMessage:"Skal udfyldes. Maks. 100 tegn"} }
                    onSave={ async (value, isValid) => {
                        if(isValid && value !== props.facility.name){
                            handleUpdateFacility({...props.facility, name:value});
                        }
                        
                    }} />
    
                <Flex gap={24} flexDirection="row" justifyContent='space-between'>
                    <FlexItem flexGrow={"1"}>
                        <TextSaveField
                            label={"Bygning"}
                            value={props.facility.building}
                            validator={(value) => fieldValidation.facility.building(value) ? {valid:true} : {valid:false, errorMessage:"Maks. 100 tegn"}}
                            onSave={ async (value, isValid) => {
                                if(isValid && value !== props.facility.building){
                                    handleUpdateFacility({...props.facility, building:value});
                                }
                                
                            }} />
                    </FlexItem>
            
                    <FlexItem flexGrow={"1"}>
                        <TextSaveField
                            label={"Etage"}
                            value={props.facility.floor}
                            validator={(value) => fieldValidation.facility.floor(value) ? {valid:true} : {valid:false, errorMessage:"Maks. 100 tegn"} }
                            onSave={ async (value, isValid) => {
                                if(isValid && value !== props.facility.floor){
                                    handleUpdateFacility({...props.facility, floor:value});
                                }
                                
                            }} />
                        </FlexItem>
                </Flex>

                <SelectField
                    options={[{text:"Vælg ansvarlig", value:""},
                        ...persons.map(c => { return {text:(c.name), value:(c.id), type:c.type}})
                                .sort((a, b) => a.text.localeCompare(b.text))
                                .filter(c => c.type === "FacilityOwner")
                            
                    ]}
                    selectedValue={props.facility.owner?.id || ''}
                    onChange={ async (value)  => {
                        handleUpdateFacility({...props.facility, owner:persons.find(c => c.id === value) || null});
                    }}
                    label="Vælg ansvarlig for faciliteten" />

                <ButtonRow variant="space-between">
                    <Button variant="destructive-text"
                        onClick={handleDeleteClick}
                        icon="interface_actions_delete"
                        text="Slet" />

                    <Button
                        text="Luk"
                        variant="filled" 
                        onClick={ props.onFacilityClose }/>

                </ButtonRow>
            </Flex>
            
        
        </>
    );
}